import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { disconnectSocket } from '../services/socket';
import ProfileModal from './ProfileModal';
import '../styles/AppMenu.css';

const games = [
  { key: 'dice', label: 'Dice', to: '/challenges' },
  { key: 'lucky', label: 'Lucky', to: '/multiplayer' },
  { key: 'whot', label: 'Whot', to: '/whot-online' },
  { key: 'chess', label: 'Chess', to: '/chess' },
  { key: 'checkers', label: 'Checkers', to: '/checkers/lobby' },
  { key: 'draughts', label: 'Draughts', to: '/draughts/lobby' },
  { key: 'flappy', label: 'Flappy', to: '/flappy' },
  { key: 'reaction', label: 'Color', to: '/reaction' },
];

export default function AppMenu() {
  const { user, updateUser, logout } = useAuth();
  const isLoggedIn = Boolean(user);
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [gamesOpen, setGamesOpen] = useState(false);
  const [drawerGamesOpen, setDrawerGamesOpen] = useState(false);
  const [friendsCount, setFriendsCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [profileModalOpen, setProfileModalOpen] = useState(false);

  useEffect(() => {
    setDrawerOpen(false);
    setGamesOpen(false);
    setDrawerGamesOpen(false);
  }, [location.pathname]);

  const mainLinks = useMemo(() => {
    if (!isLoggedIn) return [];
    return [
      { key: 'dashboard', label: 'Dashboard', to: '/dashboard' },
      { key: 'leaderboard', label: 'Leaderboard', to: '/leaderboard' },
      { key: 'ledger', label: 'Transactions', to: '/ledger' },
      { key: 'rewards', label: 'Rewards', to: '/rewards' },
      { key: 'referrals', label: 'Referrals', to: '/referrals' },
      { key: 'friends', label: 'Friends', to: '/dashboard?friends=1', badge: friendsCount },
      { key: 'messages', label: 'Messages', to: '/dashboard?messages=1', badge: unreadCount },
      { key: 'history', label: 'History', to: '/history' },
    ];
  }, [friendsCount, unreadCount, isLoggedIn]);

  useEffect(() => {
    if (!user?.id) return;
    let active = true;

    const loadCounts = async () => {
      try {
        const [friendsRes, chatsRes] = await Promise.all([
          api.get('/friends'),
          api.get('/chat/conversations')
        ]);
        if (!active) return;
        const friends = Array.isArray(friendsRes.data) ? friendsRes.data : [];
        const conversations = Array.isArray(chatsRes.data) ? chatsRes.data : [];
        const unread = conversations.reduce((sum, conv) => sum + Number(conv.unread_count || 0), 0);
        setFriendsCount(friends.length);
        setUnreadCount(unread);
      } catch {
        if (!active) return;
        setFriendsCount(0);
        setUnreadCount(0);
      }
    };

    loadCounts();
    const id = setInterval(loadCounts, 5000);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, [user?.id]);

  const handleSessionAction = () => {
    setDrawerOpen(false);
    setGamesOpen(false);
    setDrawerGamesOpen(false);

    if (user) {
      logout();
      disconnectSocket();
      navigate('/');
      return;
    }
    navigate('/login', { state: { backgroundLocation: location } });
  };

  const handleDepositAction = () => {
    setDrawerOpen(false);
    setGamesOpen(false);
    setDrawerGamesOpen(false);

    if (user) {
      navigate('/ledger');
      return;
    }

    navigate('/login', { state: { backgroundLocation: location } });
  };

  const handleOpenFriends = () => {
    setDrawerOpen(false);
    setGamesOpen(false);
    setDrawerGamesOpen(false);
    window.dispatchEvent(new Event('app:open-friends'));
  };

  const handleOpenProfile = () => {
    if (!isLoggedIn) return;
    setProfileModalOpen(true);
  };

  const handleProfileUpdate = (updatedUser) => {
    updateUser(updatedUser);
  };

  return (
    <div className="app-menu">
      <div className="app-menu__bar">
        <button
          className="app-menu__hamburger"
          type="button"
          aria-label="Open menu"
          onClick={() => setDrawerOpen(true)}
        >
          <span />
          <span />
          <span />
        </button>

        <div
          className="app-menu__brand"
          onClick={() => navigate(isLoggedIn ? '/dashboard' : '/')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') navigate(isLoggedIn ? '/dashboard' : '/');
          }}
          role="button"
          tabIndex={0}
        >
          <div className="app-menu__logo">WikiGame</div>
          <div className="app-menu__tag">Community</div>
        </div>

        <div className="app-menu__links">
          {isLoggedIn && (
            <>
              {mainLinks.map((item) => (
                item.key === 'friends' ? (
                  <button
                    key={item.key}
                    type="button"
                    className="app-menu__link app-menu__link-btn"
                    onClick={handleOpenFriends}
                  >
                    <span>{item.label}</span>
                    {item.badge > 0 && <span className="app-menu__badge">{item.badge}</span>}
                  </button>
                ) : (
                  <NavLink
                    key={item.key}
                    to={item.to}
                    className={({ isActive }) => `app-menu__link ${isActive ? 'is-active' : ''}`}
                  >
                    <span>{item.label}</span>
                    {item.badge > 0 && <span className="app-menu__badge">{item.badge}</span>}
                  </NavLink>
                )
              ))}

              <div className={`app-menu__dropdown ${gamesOpen ? 'is-open' : ''}`}>
                <button
                  className="app-menu__link app-menu__dropdown-btn"
                  type="button"
                  aria-expanded={gamesOpen ? 'true' : 'false'}
                  onClick={() => setGamesOpen((prev) => !prev)}
                >
                  Games
                  <span className="app-menu__caret">▾</span>
                </button>
                <div className="app-menu__dropdown-panel">
                  {games.map((item) => (
                    <NavLink
                      key={item.key}
                      to={item.to}
                      className={({ isActive }) => `app-menu__dropdown-item ${isActive ? 'is-active' : ''}`}
                    >
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="app-menu__actions">
          {isLoggedIn && (
            <div className="app-menu__user" onClick={handleOpenProfile} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleOpenProfile(); }}>
              <div className="app-menu__avatar">{user?.username ? user.username.slice(0, 1).toUpperCase() : 'U'}</div>
              <div className="app-menu__user-meta">
                <div className="app-menu__user-name">{user?.username || 'Player'}</div>
                <div className="app-menu__user-balance">{Number(user?.balance || 0)} wkp</div>
              </div>
            </div>
          )}
          {isLoggedIn && (
            <button className="app-menu__deposit" type="button" onClick={handleDepositAction}>
              Deposit
            </button>
          )}
          <button className="app-menu__logout" type="button" onClick={handleSessionAction}>
            {user ? 'Logout' : 'Login / Sign up'}
          </button>
        </div>
      </div>

      {drawerOpen && (
        <>
          <div className="app-menu__backdrop" onClick={() => setDrawerOpen(false)} aria-hidden="true" />
          <div className="app-menu__drawer">
            <div className="app-menu__drawer-header">
              <div>
                <div className="app-menu__logo">WikiGame</div>
                <div className="app-menu__tag">Community</div>
              </div>
              <button className="app-menu__close" type="button" onClick={() => setDrawerOpen(false)} aria-label="Close menu">
                ✕
              </button>
            </div>

            {isLoggedIn && (
              <button
                className="app-menu__deposit app-menu__drawer-session-btn"
                type="button"
                onClick={handleDepositAction}
              >
                Deposit
              </button>
            )}

            <button
              className="app-menu__logout app-menu__drawer-session-btn"
              type="button"
              onClick={handleSessionAction}
            >
              {user ? 'Logout' : 'Login / Sign up'}
            </button>

            <div className="app-menu__drawer-nav">
              {isLoggedIn && (
                <>
                  <div className="app-menu__drawer-section">
                    <div className="app-menu__drawer-title">Navigation</div>
                    {mainLinks.map((item) => (
                      item.key === 'friends' ? (
                        <button
                          key={item.key}
                          type="button"
                          className="app-menu__drawer-link app-menu__drawer-link-btn"
                          onClick={handleOpenFriends}
                        >
                          <span>{item.label}</span>
                          {item.badge > 0 && <span className="app-menu__badge">{item.badge}</span>}
                        </button>
                      ) : (
                        <NavLink
                          key={item.key}
                          to={item.to}
                          className={({ isActive }) => `app-menu__drawer-link ${isActive ? 'is-active' : ''}`}
                        >
                          <span>{item.label}</span>
                          {item.badge > 0 && <span className="app-menu__badge">{item.badge}</span>}
                        </NavLink>
                      )
                    ))}
                  </div>

                  <div className="app-menu__drawer-section">
                    <button
                      className="app-menu__drawer-title app-menu__drawer-title-btn"
                      type="button"
                      aria-expanded={drawerGamesOpen ? 'true' : 'false'}
                      onClick={() => setDrawerGamesOpen((prev) => !prev)}
                    >
                      <span>Games</span>
                      <span className="app-menu__caret">{drawerGamesOpen ? '▴' : '▾'}</span>
                    </button>
                    {drawerGamesOpen && (
                      <div className="app-menu__drawer-games">
                        {games.map((item) => (
                          <NavLink
                            key={item.key}
                            to={item.to}
                            className={({ isActive }) => `app-menu__drawer-link ${isActive ? 'is-active' : ''}`}
                          >
                            {item.label}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {isLoggedIn && (
              <div className="app-menu__drawer-footer">
                <div className="app-menu__drawer-user" onClick={handleOpenProfile} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleOpenProfile(); }}>
                  <div className="app-menu__avatar">{user?.username ? user.username.slice(0, 1).toUpperCase() : 'U'}</div>
                  <div>
                    <div className="app-menu__user-name">{user?.username || 'Player'}</div>
                    <div className="app-menu__user-balance">{Number(user?.balance || 0)} wkp</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {isLoggedIn && (
        <ProfileModal 
          user={user} 
          isOpen={profileModalOpen} 
          onClose={() => setProfileModalOpen(false)}
          onProfileUpdate={handleProfileUpdate}
        />
      )}
    </div>
  );
}
